<template>
  <div>
    <h1>Payable</h1>
    <div v-if="trips">
      <table
        class="uk-table uk-table-small uk-margin-top uk-text-left uk-table-striped"
      >
        <thead>
          <th>Order #/Container</th>
          <th>Trip Type</th>
          <th>Driver Name</th>
          <th>Origin</th>
          <th>Destination</th>
          <th>Trip Start/End</th>
          <th>Total Payable</th>
          <th v-if="this.selectedTrip"></th>
        </thead>
        <tbody v-if="!this.selectedTrip">
          <tr v-for="trip in trips" :key="trip.id" @click="selectTrip(trip)">
            <td>{{ container.containerId }}</td>
            <td>
              <TripTypeField
                :showname="false"
                :trip="trip"
                :isEditing="false"
                :darkGreen="trip.status == 'Complete'"
                :gray="
                  trip.status != 'Complete' && trip.status != 'In Progress'
                "
                :green="trip.status == 'In Progress'"
              ></TripTypeField>
            </td>
            <td>{{ trip.driver ? trip.driver.fullName : '' }}</td>
            <td>{{ trip.origin.name }}</td>
            <td>{{ trip.destination.name }}</td>
            <td>
              {{ trip.scheduledDate ? formatDate(trip.scheduledDate) : '--' }}
            </td>
            <td>
              {{ trip.totalPayable }}
            </td>
            <td>
              <Button v-if="trip.driver">Add Charges</Button>
              <Button
                v-else
                disabled
                uk-tooltip="Please add Driver before adding charges to trip"
                >Add Charges</Button
              >
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td>{{ container.containerId }}</td>
            <td>
              <TripTypeField
                :showname="false"
                :trip="selectedTrip"
                :isEditing="false"
                :darkGreen="selectedTrip.status == 'Complete'"
                :gray="
                  selectedTrip.status != 'Complete' &&
                  selectedTrip.status != 'In Progress'
                "
                :green="selectedTrip.status == 'In Progress'"
              ></TripTypeField>
            </td>
            <td>{{ selectedTrip.driver ? selectedTrip.driver.name : '' }}</td>
            <td>{{ selectedTrip.origin.name }}</td>
            <td>{{ selectedTrip.destination.name }}</td>
            <td>
              {{
                selectedTrip.scheduledDate
                  ? formatDate(selectedTrip.scheduledDate)
                  : '--'
              }}
            </td>
            <td>
              {{ selectedTrip.totalPayable }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="uk-container" v-if="this.selectedTrip">
      <table class="uk-table uk-table-small uk-margin-remove">
        <tbody>
          <tr v-for="(charge, chargeIdx) in tripCharges" :key="chargeIdx">
            <td class="uk-padding-remove">{{ charge.name }}</td>
            <td class="uk-padding-remove">
              $<input
                type="text"
                class="uk-input uk-width-small uk-form-small"
                v-model="charge.amount"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div class="uk-flex uk-flex-right">
        <Button @click="updateDriverCharges" class="uk-flex">
          Update Charges</Button
        >
        <Button @click="cancelSelectedTrip" :isGray="true" class="uk-flex">
          Back to all trips</Button
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Button from '@/components/core/Button.vue';
import sort from 'fast-sort';

import AddDriverCharges from '@/gql/orders/trips/mutations/AddDriverCharges.gql';
import { dateFormatMixin } from '../../../utils/date';
import TripTypeField from '../UI/TripTypeField.vue';

export default defineComponent({
  components: {
    Button,
    TripTypeField,
  },
  mixins: [dateFormatMixin],
  props: {
    container: {
      required: true,
    },
  },
  computed: {
    trips(): any[] {
      const vm = (this as any);

      if (vm.container) {
        return sort((vm.container as any).trips).asc((x: any) => x.legId);
      } else {
        return [];
      }
    },
  },
  methods: {
    cancelSelectedTrip() {
      const vm = (this as any);

      vm.selectedTrip = undefined;
    },
    selectTrip(trip) {
      const vm = (this as any);

      vm.selectedTrip = trip;
      if (vm.selectedTrip) {
        const mappedCharges = vm.selectedTrip.driverCharges.map((x) => {
          return {
            amount: x.amount,
            name: x.name,
          };
        });

        const merged = [];

        // loops through the objects in padding. If it finds an object in data that has the same t value, it chooses the object from data to add to the merged array. Otherwise, it uses the object from padding

        vm.tripCharges.forEach((obj) => {
          const dataObj = mappedCharges.find((ob) => ob.name === obj.name);
          if (dataObj) {
            merged.push(dataObj);
          } else {
            merged.push(obj);
          }
        });
        vm.tripCharges = merged;
      }
    },
    updateDriverCharges() {
      const vm = (this as any);

      const finalCharges = vm.tripCharges
        .filter((x) => x.amount)
        .map((x) => {
          return { name: x.name, amount: Number(x.amount) };
        });
      vm.$apollo.mutate({
        mutation: AddDriverCharges,
        variables: {
          id: vm.selectedTrip.id,
          charges: finalCharges,
        },
      });
      vm.selectedTrip = undefined;
    },
  },
  data() {
    return {
      selectedTrip: undefined,

      tripCharges: [
        {
          name: 'Driver Pay',
          amount: undefined,
        },
        {
          name: 'Advance Pay',
          amount: undefined,
        },
        {
          name: 'Extra Stops',
          amount: undefined,
        },
        {
          name: 'Lumper Charges',
          amount: undefined,
        },
        {
          name: 'Detention Charges',
          amount: undefined,
        },
        {
          name: 'Chassis Split',
          amount: undefined,
        },
        {
          name: 'Chassis Rent',
          amount: undefined,
        },
        {
          name: 'Other Charges',
          amount: undefined,
        },
      ],
    };
  },
});
</script>

<style lang="scss" scoped>
td {
  @extend .uk-margin-remove !optional;
}
</style>